import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { Permission } from '../../types/auth';
import { Spinner } from '../UI/Spinner';

interface ProtectedRouteProps {
  requiredPermission?: Permission;
  requireSuperadmin?: boolean;
}

/**
 * Protected route wrapper that checks authentication and permissions
 *
 * Usage:
 * <Route element={<ProtectedRoute requiredPermission="read" />}>
 *   <Route path="/profile" element={<Profile />} />
 * </Route>
 *
 * <Route element={<ProtectedRoute requireSuperadmin />}>
 *   <Route path="/dashboard" element={<Dashboard />} />
 * </Route>
 */
export function ProtectedRoute({ requiredPermission, requireSuperadmin }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasPermission, user } = useAuth();

  // Show loading spinner while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check superadmin if required
  if (requireSuperadmin && !user?.is_superadmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow rounded-lg p-6">
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h2 className="mt-4 text-lg font-medium text-gray-900">
              Access Denied
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              This page is restricted to administrators.
            </p>
            <div className="mt-6">
              <a
                href="/"
                className="text-primary-600 hover:text-primary-500 font-medium"
              >
                Go Home
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check permission if required
  if (requiredPermission && !hasPermission(requiredPermission)) {
    // User is authenticated but doesn't have required permission
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow rounded-lg p-6">
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h2 className="mt-4 text-lg font-medium text-gray-900">
              Access Denied
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              You don't have permission to access this page.
              {requiredPermission && ` Required permission: ${requiredPermission}`}
            </p>
            <div className="mt-6">
              <a
                href="/dashboard"
                className="text-primary-600 hover:text-primary-500 font-medium"
              >
                Go to Dashboard
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render child routes
  return <Outlet />;
}
